import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { LoadingOverlay } from '@repletoryreach/sdk-ui/feedback';
import type { ReactElement, ReactNode } from 'react';
import { useT } from '../../i18n/I18nProvider';

/**
 * Gate for the authenticated app.
 *
 * Three behaviours, all of which used to be missing (see
 * `_UI_improvements/tenant/_app-shell.md` §4 items 1–3):
 *
 * 1. A `platform`-scope token is refused. `RequireUnauthed` always checked the
 *    scope; this side did not, so an admin token in this origin's storage
 *    rendered the whole tenant shell against admin claims.
 * 2. The attempted location is carried to the login page in `state.from`, so a
 *    deep link survives the sign-in bounce instead of dumping the user on the
 *    dashboard.
 * 3. The pending state is a real overlay with a catalogued label rather than a
 *    bare hardcoded "Loading…".
 */
export function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();
  const t = useT();

  if (loading) {
    return (
      <div className="rr-page-loading">
        <LoadingOverlay visible label={t('shell.loading')} />
      </div>
    );
  }

  // No session, or a session that does not belong to this console.
  if (!user || user.scope !== 'tenant') {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
